import {
  ApolloClient,
  ApolloLink,
  from,
  HttpLink,
  InMemoryCache,
  Observable,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { onError } from '@apollo/client/link/error';
import * as Sentry from '@sentry/react';
import _some from 'lodash/some';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import { getSessionTokenCookie } from '@lib/utils/cookieUtils';
import {
  createCustomFetch,
  FETCH_TIMEOUT,
  getNetworkContext,
  isRetryableError,
  delay,
  MAX_RETRIES,
  RETRY_DELAY_MS,
} from '@lib/utils/customFetch';
import { captureException } from 'utils/captureException';

const { VITE_API_URL: API_DOMAIN, MODE } = import.meta.env;

if (MODE && ['development', 'test'].includes(MODE)) {
  loadDevMessages();
  loadErrorMessages();
}

const customHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
  'X-App-Component': 'system',
  'Cache-Control': 'no-cache',
};

const cors = {
  xhrFields: { withCredentials: true },
  crossDomain: true,
  mode: 'cors',
  credentials: 'include',
};

/**
 * Performs a single ServiceWorker fetch attempt
 */
function performServiceWorkerFetch(
  uri: RequestInfo | URL,
  options: RequestInit | undefined,
): Promise<Response> {
  return new Promise<Response>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(
        new Error(`ServiceWorker request timed out after ${FETCH_TIMEOUT}ms`),
      );
    }, FETCH_TIMEOUT);

    const messageChannel = new MessageChannel();

    messageChannel.port1.onmessage = (event) => {
      clearTimeout(timeoutId);
      if (event.data.error) {
        const errorMessage = event.data.error?.message;
        // Return error response instead of rejecting to match Apollo's expectations
        resolve(
          new Response(
            JSON.stringify({
              errors: [{ message: errorMessage }],
            }),
            {
              status: event.data.status || 503,
              headers: { 'Content-Type': 'application/json' },
            },
          ),
        );
      } else {
        const fetchResponse = new Response(new Blob([event.data.body]), {
          status: event.data.status,
          statusText: event.data.statusText,
          headers: event.data.headers,
        });

        const requestId = fetchResponse.headers.get('x-request-id');
        if (requestId) {
          Sentry.setTag('request_id', requestId);
        }

        resolve(fetchResponse);
      }
    };

    if ('serviceWorker' in navigator) {
      const sanitizedOptions = { ...options };
      delete sanitizedOptions.signal;
      const serviceWorker = navigator.serviceWorker as ServiceWorkerContainer;
      serviceWorker.controller?.postMessage(
        { type: 'graphql-request', uri, options: sanitizedOptions },
        [messageChannel.port2],
      );
    }
  });
}

/**
 * Custom fetch with ServiceWorker integration and retry logic
 * Uses ServiceWorker for GraphQL requests when available, falls back to
 * createCustomFetch with retry logic for direct fetch calls
 */
const customFetchWithServiceWorker = async (
  uri: RequestInfo | URL,
  options?: RequestInit,
): Promise<Response> => {
  const isServiceWorkerAvailable =
    'serviceWorker' in navigator &&
    (navigator.serviceWorker as ServiceWorkerContainer).controller;

  // Use ServiceWorker for non-OPTIONS requests when available
  if (options?.method !== 'OPTIONS' && isServiceWorkerAvailable) {
    let lastError: Error | null = null;

    // eslint-disable-next-line no-plusplus
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await performServiceWorkerFetch(uri, options);
        return response;
      } catch (error) {
        const errorInstance =
          error instanceof Error ? error : new Error(String(error));
        lastError = errorInstance;

        // Add context to Sentry
        Sentry.setContext('fetchError', {
          errorType: 'SERVICE_WORKER_ERROR',
          attempt: attempt + 1,
          maxRetries: MAX_RETRIES,
          timeout: FETCH_TIMEOUT,
          uri: typeof uri === 'string' ? uri : uri.toString(),
          errorMessage: errorInstance.message,
          ...getNetworkContext(),
        });

        // Retry for transient errors
        if (attempt < MAX_RETRIES && isRetryableError(error)) {
          const backoffDelay = RETRY_DELAY_MS * (attempt + 1);
          // eslint-disable-next-line no-await-in-loop
          await delay(backoffDelay);
          // eslint-disable-next-line no-continue
          continue;
        }

        throw errorInstance;
      }
    }

    throw (
      lastError || new Error('ServiceWorker fetch failed after all retries')
    );
  }

  // Fall back to standard customFetch with retry logic
  return createCustomFetch(uri, options);
};

const singleHttpLink = new HttpLink({
  uri: `${API_DOMAIN}/graphql`,
  headers: customHeaders,
  fetch: customFetchWithServiceWorker,
});

const httpLink = new BatchHttpLink({
  uri: `${API_DOMAIN}/graphql`,
  batchMax: 5,
  batchInterval: 20,
  headers: customHeaders,
  fetch: customFetchWithServiceWorker,
  ...cors,
});

const authLink = setContext((_, { headers }) => {
  const token = getSessionTokenCookie();
  if (!token) {
    return {
      headers: {
        ...headers,
      },
    };
  }
  // return the headers to be used in the HTTP request
  return {
    headers: {
      ...headers,
      AUTHORIZATION: `Bearer ${token}`,
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  const isProblemExist = _some(
    graphQLErrors,
    ({ extensions }) => !_isEmpty(extensions?.problems),
  );
  const isUndefinedField = _some(graphQLErrors, ({ extensions }) =>
    _isEqual(extensions?.code, 'undefinedField'),
  );
  if (graphQLErrors && (isProblemExist || isUndefinedField)) {
    graphQLErrors.forEach((error) => {
      captureException(
        new Error(`[GraphQL error]: Message: ${error.message}`),
        error,
      );
    });
  }

  const statusCode = _get(networkError, 'statusCode');
  if (networkError && statusCode !== 429) {
    captureException(networkError);
  }
});

let hasFetchedToken = false;
const optionsRequestLink = new ApolloLink((operation, forward) => {
  if (hasFetchedToken) {
    // If token has been fetched, proceed with the operation
    return forward(operation);
  }
  return new Observable((observer) => {
    // Send an OPTIONS request
    fetch('/graphql', {
      method: 'OPTIONS',
      credentials: 'include',
    })
      .then(() => {
        hasFetchedToken = true;
      })
      .finally(() => {
        // Proceed with the original request
        forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        });
      })
      .catch((error) => {
        observer.error(error);
      });
  });
});

const sentryTracingLink = new ApolloLink((operation, forward) => {
  return Sentry.startSpan(
    { name: operation.operationName || 'GraphQL Request', op: 'graphql' },
    (span) => {
      return forward(operation).map((response) => {
        span?.end();
        return response;
      });
    },
  );
});

const customLink = new ApolloLink((operation, forward) => {
  const { operationName } = operation;
  return ['appFullTask', 'taskCompletionCreate', 'Whoami'].includes(
    operationName,
  )
    ? singleHttpLink.request(operation, forward)
    : httpLink.request(operation);
});

const link = from([
  sentryTracingLink,
  optionsRequestLink,
  authLink,
  errorLink,
  customLink,
]);

const Client = new ApolloClient({
  cache: new InMemoryCache(),
  link,
  defaultOptions: {
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
  },
});

export default Client;
